import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Avatar, Chip, Button
} from '@mui/material';
import {
  Language as LanguageIcon, School as SchoolIcon, Star,
  Verified as VerifiedIcon, Edit
} from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getCurrentLanguage } from '../../utils/navigation';

const TeacherProfileView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = getCurrentLanguage();

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 'EUR': return '€';
      case 'MAD': return 'د.م.';
      case 'GBP': return '£';
      default: return '$';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>My Profile</Typography>
        <Button
          variant="outlined" startIcon={<Edit />}
          onClick={() => navigate(`/${lang}/teacher/settings`)}
        >
          Edit Profile
        </Button>
      </Box>

      {/* Profile Card */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3} textAlign="center">
            <Avatar
              src={user?.photoURL}
              sx={{ width: 140, height: 140, mx: 'auto', fontSize: 56 }}
            >
              {user?.displayName?.charAt(0)}
            </Avatar>
            {user?.profileApproved ? (
              <Chip icon={<VerifiedIcon />} label="Approved" color="success" sx={{ mt: 1 }} />
            ) : (
              <Chip label="Pending Approval" color="warning" sx={{ mt: 1 }} />
            )}
          </Grid>
          <Grid item xs={12} sm={9}>
            <Typography variant="h4" fontWeight={700}>{user?.displayName}</Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>{user?.country}</Typography>

            <Box sx={{ display: 'flex', gap: 3, my: 2 }}>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight={700}>{user?.totalStudents || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Students</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h5" fontWeight={700}>{user?.totalLessons || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Lessons</Typography>
              </Box>
              <Box textAlign="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Star sx={{ color: '#FFB400' }} />
                  <Typography variant="h5" fontWeight={700}>
                    {user?.averageRating?.toFixed(1) || '—'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">Rating</Typography>
              </Box>
            </Box>

            <Typography variant="h6" color="primary.main" fontWeight={700}>
              {getCurrencySymbol(user?.currency)}{user?.pricePerHour} / {user?.lessonDuration || 50} min
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Details */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Bio</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {user?.bio || 'No bio set'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Languages</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {user?.languages?.map(l => (
                <Chip
                  key={l} label={l} icon={<LanguageIcon />}
                  color={user?.nativeLanguages?.includes(l) ? 'success' : 'default'}
                  variant="outlined"
                />
              )) || <Typography color="text.secondary">No languages set</Typography>}
            </Box>

            <Typography variant="h6" fontWeight={600} gutterBottom>Specialties</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {user?.specialties?.map(s => (
                <Chip key={s} label={s} icon={<SchoolIcon />} color="primary" variant="outlined" />
              )) || <Typography color="text.secondary">No specialties set</Typography>}
            </Box>
          </Paper>
        </Grid>

        {(user?.experience || user?.education) && (
          <>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Experience</Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {user?.experience || 'Not specified'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>Education</Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {user?.education || 'Not specified'}
                </Typography>
              </Paper>
            </Grid>
          </>
        )}

        {user?.certifications?.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>Certifications</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {user.certifications.map((cert, i) => (
                  <Chip key={i} label={cert} icon={<VerifiedIcon />} variant="outlined" />
                ))}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TeacherProfileView;
